import { FilterFacetLayout } from '@core/enums';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import styled, { css } from 'styled-components';
import { ProductDetailsContentStyles } from '../ProductDetailsContent/ProductDetailsContent.styles';

const Variants = styled.div<{ layout: FilterFacetLayout }>`
  display: grid;
  grid-gap: 8px;
  margin: 12px 0px 20px 0px;
  grid-auto-flow: column;

  @media ${until(Device.Tablet)} {
    display: flex;
    flex-wrap: wrap;
  }

  ${({ layout }) => {
    switch (layout) {
      case FilterFacetLayout.Buttons: {
        return css`
          display: flex;
          flex-wrap: wrap;
        `;
      }

      case FilterFacetLayout.Colours: {
        return css`
          @media ${until(Device.Tablet)} {
            display: grid;
            grid-template-columns: repeat(4, 40px);
            grid-auto-flow: row;
          }

          @media ${from(Device.Tablet)} {
            grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
            grid-auto-flow: row;
          }

          @media ${from(Device.DesktopLarge)} {
            grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
          }
        `;
      }

      case FilterFacetLayout.Thumbnails: {
        return css`
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          --thumbnailWidth: 49px;

          @media ${from(Device.TabletLarge)} {
            --thumbnailWidth: 65px;
            gap: 17px;
          }
        `;
      }
    }
  }}
`;

const Button = styled.button.attrs({ type: 'button' })<{ isActive: boolean; isDiabled: boolean }>`
  ${ButtonReset};
  border: 1px solid ${brand.grey[80]};
  background: ${brand.white};
  padding: 8px;
  font-size: 15px;
  ${fonts.helvetica['87-heavy-condensed']};
  border-radius: 4px;
  white-space: nowrap;
  text-align: center;
  position: relative;
  cursor: pointer;
  min-width: 78px;

  ${({ isDiabled }) =>
    isDiabled
      ? css`
          background: ${brand.grey[80]};
          border: 1px solid ${brand.grey[80]};
          color: ${brand.black};

          span {
            opacity: 0.5;
          }
        `
      : null}

  ${({ isActive }) =>
    isActive
      ? css`
          background: ${brand.red.base};
          border: 1px solid ${brand.red.base};
          color: ${brand.white};
        `
      : css``}

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:after {
    content: attr(data-longest-value);
    position: relative;
    opacity: 0;
  }
`;

const Thumbnail = styled.button.attrs({ type: 'button' })<{ isActive: boolean; isDisabled: boolean }>`
  ${ButtonReset};
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: var(--thumbnailWidth);
  aspect-ratio: 1 / 1;
  border-radius: 4px;

  @media ${from(Device.TabletLarge)} {
  }

  ${({ isActive }) =>
    isActive
      ? css`
          border: 2px solid ${brand.black};
        `
      : css`
          border: 2px solid transparent;

          &:hover {
            border: 2px solid ${brand.grey[90]};
          }
        `}

  ${({ isDisabled }) =>
    isDisabled
      ? css`
          opacity: 0.4;
        `
      : null}

  img {
    display: block;
    aspect-ratio: 1 / 1;
    border-radius: 4px;
  }
`;

const Colour = styled.button.attrs({ type: 'button' })<{ isActive: boolean; isDiabled: boolean }>`
  ${ButtonReset};
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;

  ${({ isActive }) =>
    isActive
      ? css`
          border: 2px solid ${brand.red.base};
        `
      : css`
          border: 1px solid ${brand.grey[75]};
        `}
`;

const ColourSquare = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 2px;
`;

const AvailableAsSetWrapper = styled.div`
  margin-top: 15px;
`;

const AvailableAsSet = styled.div<{ isActive: boolean }>`
  ${ButtonReset};
  background: ${brand.white};
  padding: 8px;
  font-size: 15px;
  ${fonts.helvetica['87-heavy-condensed']};
  border-radius: 4px;
  white-space: nowrap;
  text-align: center;
  position: relative;
  cursor: pointer;
  min-width: 78px;
  width: 86px;
  margin-top: 12px;

  ${({ isActive }) =>
    isActive
      ? css`
          border: 2px solid ${brand.red.base};
        `
      : css`
          border: 2px solid ${brand.grey[80]};
        `}

  img {
    width: 100%;
    height: 100%;
  }
`;

const SetIncludes = styled.div`
  font-size: 15px;
`;

const SetIncludesHeading = styled(ProductDetailsContentStyles.H4)`
  font-size: 18px;
`;

export const ProductDetailsContentVariantSelectorStyles = {
  Variants,
  Button,
  Colour,
  ColourSquare,
  Thumbnail,
  AvailableAsSetWrapper,
  AvailableAsSet,
  SetIncludes,
  SetIncludesHeading,
};
